import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Button, Badge, Spinner, Alert, Dropdown, ButtonGroup, Modal, Form } from 'react-bootstrap';
import { useThree3D } from '../hooks/useThree3D';
import { useRailwayData3D } from '../hooks/useRailwayData3D';
import { Hotspot, AnalysisData, Point3D } from '../service/railwayDataService';

export interface Railway3DChartProps {
  // Datos del sistema railway
  hotspots: Hotspot[];
  analysisData?: AnalysisData | null;
  
  // Configuración del gráfico
  chartType?: 'hotspots' | 'technical' | 'combined';
  selectedMetrics?: string[];
  height?: number;
  
  // Configuración visual
  showControls?: boolean;
  showLegend?: boolean;
  showStatistics?: boolean;
  enableAutoRotate?: boolean;
  enableAnimation?: boolean;
  
  // Configuración de filtros
  filterOptions?: {
    priorities?: string[];
    riskRange?: [number, number];
    defectCountRange?: [number, number];
    kpRange?: [number, number];
  };
  
  // Configuración de hotspots
  hotspotsLimit?: '1-50' | '51-150' | '151-250' | '251-500' | 'unlimited';
  
  // Callbacks - NUEVOS para integración con mapa
  onPointClick?: (point: Point3D) => void;
  onPointHover?: (point: Point3D | null) => void;
  onDataUpdate?: (totalPoints: number, visiblePoints: number) => void;
  onError?: (error: string) => void;
  onHotspotSelect?: (hotspot: Hotspot) => void; // NUEVO: Para abrir en mapa
  
  // Configuración avanzada
  autoRefresh?: boolean;
  refreshInterval?: number;
  backgroundColor?: string;
  cameraPosition?: { x: number; y: number; z: number };
}

export const Railway3DChart: React.FC<Railway3DChartProps> = ({
  hotspots,
  analysisData = null,
  chartType = 'hotspots',
  selectedMetrics = ['geometry', 'ai_risk', 'wear'],
  height = 500,
  showControls = true,
  showLegend = true,
  showStatistics = true,
  enableAutoRotate = false,
  enableAnimation = true,
  filterOptions,
  hotspotsLimit = 'unlimited', // CAMBIO: unlimited por defecto
  onPointClick,
  onPointHover,
  onDataUpdate,
  onError,
  onHotspotSelect, // NUEVO
  autoRefresh = false,
  refreshInterval = 30000,
  backgroundColor = '',
  cameraPosition = { x: 50, y: 40, z: 50 }
}) => {
  
  const [lastErrorNotified, setLastErrorNotified] = useState<string | null>(null);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(enableAutoRotate);

  // NUEVO: Estado para el modal de detalles del hotspot
  const [selectedHotspot, setSelectedHotspot] = useState<Hotspot | null>(null);
  const [showHotspotModal, setShowHotspotModal] = useState(false);

  // Estado para debugging
  const [debugInfo, setDebugInfo] = useState<any>({});

  // Hook para gestión de datos railway 3D
  const railwayData = useRailwayData3D(hotspots, analysisData, {
    chartType,
    selectedMetrics,
    filterOptions,
    autoRefresh,
    refreshInterval,
    hotspotsLimit
  });

  // Hook para gestión de Three.js
  const {
    containerRef,
    isLoading,
    error: threeError,
    tooltip,
    updateData,
    resetCamera,
    clearScene,
    exportImage,
    toggleAutoRotate,
    setSelectedMetrics,
    statistics: threeStatistics
  } = useThree3D({
    enableControls: showControls,
    enableTooltips: true,
    backgroundColor,
    cameraPosition,
    onPointClick: handlePointClick, // Usar nuestro handler personalizado
    onPointHover: handlePointHover, // Usar nuestro handler personalizado
    autoRotate: autoRotateEnabled,
    enableAnimation
  });

  // NUEVO: Logging detallado para debugging
  useEffect(() => {
    const debug = {
      timestamp: new Date().toLocaleTimeString(),
      railwayDataExists: !!railwayData.data,
      pointsLength: railwayData.data?.points?.length || 0,
      isLoading,
      isProcessing: railwayData.isProcessing,
      threeError,
      railwayError: railwayData.error,
      chartType,
      hotspotCount: hotspots?.length || 0
    };
    
    setDebugInfo(debug);
    
    console.log('🎯 Railway3DChart Debug State:', debug);
    
    if (railwayData.data?.points) {
      console.log('📊 Sample points:', railwayData.data.points.slice(0, 2));
    }
  }, [
    railwayData.data, 
    railwayData.isProcessing, 
    railwayData.error,
    isLoading, 
    threeError, 
    chartType, 
    hotspots?.length
  ]);

  // Combinar errores
  const combinedError = threeError || railwayData.error;

  // NUEVO: Determinar si está vacío (como en LineChart)
  const isEmpty = !isLoading && 
                  !railwayData.isProcessing && 
                  (!hotspots || hotspots.length === 0) && 
                  (!railwayData.data || railwayData.data.points.length === 0);

  // useEffect para actualizar datos
  useEffect(() => {
    console.log('🔄 Data update effect triggered:', {
      isLoading,
      hasData: !!railwayData.data,
      pointsLength: railwayData.data?.points?.length || 0,
      isProcessing: railwayData.isProcessing
    });

    if (isLoading) {
      console.log('⏳ Three.js still loading, waiting...');
      return;
    }

    if (!railwayData.data) {
      console.log('❌ No railway data available');
      return;
    }

    if (!railwayData.data.points || !Array.isArray(railwayData.data.points)) {
      console.log('❌ Invalid points data structure');
      return;
    }

    if (railwayData.data.points.length > 0) {
      console.log('✅ Updating 3D scene with', railwayData.data.points.length, 'points');
      try {
        updateData(railwayData.data);
        console.log('✅ Data updated successfully');
      } catch (error) {
        console.error('❌ Error updating data:', error);
      }
    } else {
      console.log('🗑️ No points to display, clearing scene');
      try {
        clearScene();
        console.log('✅ Scene cleared successfully');
      } catch (error) {
        console.error('❌ Error clearing scene:', error);
      }
    }
  }, [railwayData.data, isLoading]);

  // NUEVO: Función para manejar click en puntos (como en LineChart)
  function handlePointClick(point: Point3D) {
    console.log('🖱️ Punto clickeado:', point);
    
    if (point.metadata.type === 'hotspot') {
      // Crear objeto hotspot desde los metadatos
      const hotspot: Hotspot = {
        id: point.metadata.id,
        location: point.metadata.location,
        defect_count: point.metadata.defect_count || 0,
        risk_score: point.metadata.risk_score || 0,
        recommendation_priority: point.metadata.recommendation_priority || 'UNKNOWN',
        coordinates: point.metadata.coordinates,
        dominant_defects: point.metadata.dominant_defects || [],
        severity_breakdown: point.metadata.severity_breakdown,
        maintenance_history: point.metadata.maintenance_history
      };

      // Mostrar modal con detalles del hotspot
      setSelectedHotspot(hotspot);
      setShowHotspotModal(true);

      // Callback para integración con mapa
      onHotspotSelect?.(hotspot);
    }
    
    // Callback original
    onPointClick?.(point);
  }

  // NUEVO: Función para manejar hover en puntos
  function handlePointHover(point: Point3D | null) {
    onPointHover?.(point);
  }

  // Actualizar métricas seleccionadas en Three.js
  useEffect(() => {
    if (chartType === 'technical' || chartType === 'combined') {
      console.log('🔧 Updating selected metrics:', railwayData.filters.selectedMetrics);
      setSelectedMetrics(railwayData.filters.selectedMetrics);
    }
  }, [railwayData.filters.selectedMetrics, chartType, setSelectedMetrics]);

  // Callback para notificar cambios de datos
  useEffect(() => {
    if (onDataUpdate && threeStatistics) {
      onDataUpdate(
        railwayData.statistics.totalPoints,
        threeStatistics.visiblePoints || 0
      );
    }
  }, [
    railwayData.statistics.totalPoints,
    threeStatistics?.visiblePoints,
    onDataUpdate
  ]);

  // Notificar errores
  useEffect(() => {
    if (combinedError && combinedError !== lastErrorNotified && onError) {
      console.error('❌ Error occurred:', combinedError);
      onError(combinedError);
      setLastErrorNotified(combinedError);
    }
  }, [combinedError, lastErrorNotified, onError]);

  // Determinar título y subtítulo
  const getChartTitle = () => {
    switch (chartType) {
      case 'hotspots':
        return 'Análisis 3D de Hotspots Críticos';
      case 'technical':
        return 'Análisis Técnico Ferroviario 3D';
      case 'combined':
        return 'Vista Combinada 3D - Hotspots & Técnico';
      default:
        return 'Gráfico 3D Railway';
    }
  };

  const getChartSubtitle = () => {
    if (!railwayData.data || railwayData.data.points.length === 0) {
      return 'Esperando datos para visualización 3D';
    }
    
    const { points, axes } = railwayData.data;
    return `${points.length} puntos de datos • ${axes.x.label} vs ${axes.y.label} vs ${axes.z.label}`;
  };

  // Handle export
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const imageUrl = await exportImage();
      
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `railway-3d-chart-${chartType}-${new Date().getTime()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error exportando imagen:', err);
      alert('Error al exportar la imagen');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle auto rotate toggle
  const handleAutoRotateToggle = () => {
    setAutoRotateEnabled(!autoRotateEnabled);
    toggleAutoRotate();
  };

  // Get quality badge variant
  const getQualityBadgeVariant = (quality: string) => {
    switch (quality) {
      case 'excellent': return 'success';
      case 'good': return 'primary';
      case 'poor': return 'warning';
      case 'empty': return 'secondary';
      default: return 'secondary';
    }
  };

  // NUEVO: Función para obtener color de prioridad
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL': return 'danger';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'secondary';
    }
  };

  // NUEVO: Función para cerrar modal de hotspot
  const handleCloseHotspotModal = () => {
    setShowHotspotModal(false);
    setSelectedHotspot(null);
  };

  // Obtener colores para leyenda
  const priorityColors = {
    'CRITICAL': '#dc3545',
    'HIGH': '#fd7e14',
    'MEDIUM': '#ffc107',
    'LOW': '#198754',
    'UNKNOWN': '#6c757d'
  };

  const metricColors = {
    'geometry': '#0066cc',
    'ai_risk': '#ff0f00',
    'wear': '#28a745',
    'dynamic': '#6f42c1',
    'fatigue': '#fd7e14',
    'compliance': '#c6b502'
  };

  // Limites de hotspots disponibles
  const hotspotsLimits = {
    '1-50': '1-50 hotspots',
    '51-150': '51-150 hotspots',
    '151-250': '151-250 hotspots',
    '251-500': '251-500 hotspots',
    'unlimited': 'Sin límite'
  };

  return (
    <Card className="mb-4">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-0">
              <i className="fas fa-cube me-2"></i>
              {getChartTitle()}
              {railwayData.data?.points?.length === 0 && !isLoading && (
                <Badge bg="warning" className="ms-2">Sin datos</Badge>
              )}
            </h5>
            <p className="text-body-tertiary mb-0">{getChartSubtitle()}</p>
          </div>
          
          {/* Chart Type Badge */}
          <div className="d-flex align-items-center gap-2">
            <Badge 
              bg={chartType === 'combined' ? 'primary' : chartType === 'hotspots' ? 'danger' : 'info'}
              className="text-uppercase"
            >
              {chartType === 'combined' ? '🔄 Combinado' : 
               chartType === 'hotspots' ? '🎯 Hotspots' : 
               '📊 Técnico'}
            </Badge>
            
            {/* Data Quality Badge */}
            <Badge bg={getQualityBadgeVariant(railwayData.statistics.dataQuality)}>
              📈 {railwayData.statistics.dataQuality === 'excellent' ? 'Excelente' :
                   railwayData.statistics.dataQuality === 'good' ? 'Bueno' :
                   railwayData.statistics.dataQuality === 'poor' ? 'Limitado' : 'Vacío'}
            </Badge>

            {/* Hotspots Limit Badge */}
            <Badge bg="info">
              🎯 {hotspotsLimits[railwayData.filters.hotspotsLimit]}
            </Badge>

            {/* Debug Badge (solo en desarrollo) */}
            {process.env.NODE_ENV === 'development' && (
              <Badge bg="dark" title="Debug Info">
                🐛 {debugInfo.pointsLength || 0}pts
              </Badge>
            )}
          </div>
        </div>

        {/* Controls Row */}
        {showControls && (
          <Row className="g-2 align-items-center">
            <Col xs="auto">
              <ButtonGroup size="sm">
                <Button 
                  variant="outline-primary"
                  onClick={resetCamera}
                  title="Resetear cámara"
                >
                  <i className="fas fa-home"></i>
                </Button>
                
                <Button 
                  variant={autoRotateEnabled ? "primary" : "outline-primary"}
                  onClick={handleAutoRotateToggle}
                  title="Auto rotación"
                >
                  <i className="fas fa-sync-alt"></i>
                </Button>
                
                <Button 
                  variant="outline-success"
                  onClick={handleExport}
                  disabled={isExporting}
                  title="Exportar imagen"
                >
                  {isExporting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fas fa-download"></i>
                  )}
                </Button>
              </ButtonGroup>
            </Col>

            {/* Hotspots Limit Control */}
            <Col xs="auto">
              <Dropdown>
                <Dropdown.Toggle variant="outline-info" size="sm">
                  <i className="fas fa-list-ol me-1"></i>
                  {hotspotsLimits[railwayData.filters.hotspotsLimit]}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {Object.entries(hotspotsLimits).map(([key, label]) => (
                    <Dropdown.Item 
                      key={key}
                      active={railwayData.filters.hotspotsLimit === key}
                      onClick={() => railwayData.actions.updateHotspotsLimit(key)}
                    >
                      {label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>

            <Col xs="auto">
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={() => setShowFiltersModal(true)}
              >
                <i className="fas fa-filter me-1"></i>
                Filtros ({Object.values(railwayData.filters).flat().length})
              </Button>
            </Col>

            <Col xs="auto">
              <Button 
                variant="outline-info" 
                size="sm"
                onClick={railwayData.actions.refreshData}
                disabled={railwayData.isProcessing}
              >
                {railwayData.isProcessing ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <i className="fas fa-refresh"></i>
                )}
              </Button>
            </Col>

            {/* Quick Stats */}
            <Col>
              <div className="d-flex gap-3 text-body-tertiary small">
                <span>
                  <strong>{railwayData.data?.points?.length || 0}</strong> puntos
                </span>
                <span>
                  <strong>{railwayData.metadata.processingTime.toFixed(1)}</strong>ms
                </span>
                <span>
                  <strong>{railwayData.metadata.filteredHotspots}</strong>/{railwayData.metadata.totalHotspots} hotspots
                </span>
                <span>
                  <i className="fas fa-clock me-1"></i>
                  {railwayData.metadata.lastUpdated.toLocaleTimeString()}
                </span>
              </div>
            </Col>
          </Row>
        )}
      </Card.Header>

      <Card.Body className="p-0 bg">
        {/* Loading State */}
        {isLoading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
            <Spinner animation="border" />
            <span className="ms-2">Procesando análisis 3D...</span>
          </div>
        )}

        {/* Empty State - IGUAL AL LINECHART */}
        {isEmpty && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
            <div className="text-center">
              <i className="fas fa-cube text-muted fs-1 mb-4"></i>
              <h5 className="text-muted mb-3">Sin datos para análisis 3D</h5>
              <p className="text-body-tertiary mb-4">
                El gráfico 3D se generará automáticamente<br/>
                una vez que se carguen y procesen documentos VTI
              </p>
              <div className="row g-2 justify-content-center">
                <div className="col-auto">
                  <div className="p-3 border rounded bg-body">
                    <i className="fas fa-upload text-primary fs-4 mb-2"></i>
                    <p className="mb-0 small">1. Cargar VTI</p>
                  </div>
                </div>
                <div className="col-auto d-flex align-items-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
                <div className="col-auto">
                  <div className="p-3 border rounded bg-body">
                    <i className="fas fa-cogs text-info fs-4 mb-2"></i>
                    <p className="mb-0 small">2. Procesar</p>
                  </div>
                </div>
                <div className="col-auto d-flex align-items-center">
                  <i className="fas fa-arrow-right text-muted"></i>
                </div>
                <div className="col-auto">
                  <div className="p-3 border rounded bg-body">
                    <i className="fas fa-cube text-success fs-4 mb-2"></i>
                    <p className="mb-0 small">3. Visualizar 3D</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error State */}
        {combinedError && !isEmpty && (
          <Alert variant="danger" className="m-3">
            <Alert.Heading>Error en el gráfico 3D</Alert.Heading>
            {combinedError}
          </Alert>
        )}

        {/* 3D Chart Container - SIEMPRE RENDERIZADO */}
        <div className="position-relative">
          <div style={{ height: `${height}px`, position: 'relative' }}>
            <div
              ref={containerRef}
              className="w-100 h-100 position-relative"
              style={{
                minHeight: `${height}px`,
                background: 'transparent',
                borderRadius: '0.375rem',
                overflow: 'hidden',
                display: (!isLoading && !isEmpty && !combinedError) ? 'block' : 'none'
              }}
            >
              {/* Loading overlay específico del gráfico */}
              {railwayData.isProcessing && (
                <div 
                  className="position-absolute top-50 start-50 translate-middle"
                  style={{ zIndex: 5 }}
                >
                  <div className="text-center text-white">
                    <div className="spinner-border spinner-border-sm mb-2" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <div className="small">Procesando datos...</div>
                  </div>
                </div>
              )}

              {/* Overlay de información técnica (en desarrollo) */}
              {process.env.NODE_ENV === 'development' && (
                <div 
                  className="position-absolute top-0 start-0 m-2 text-white small"
                  style={{ 
                    zIndex: 3, 
                    backgroundColor: 'rgba(0,0,0,0.8)', 
                    padding: '8px', 
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '11px'
                  }}
                >
                  <div>FPS: {threeStatistics?.fps || 0}</div>
                  <div>Visible: {threeStatistics?.visiblePoints || 0}/{threeStatistics?.totalPoints || 0}</div>
                  <div>Type: {chartType}</div>
                  <div>Loading: {isLoading ? 'YES' : 'NO'}</div>
                  <div>Points: {railwayData.data?.points?.length || 0}</div>
                </div>
              )}

              {/* Indicador de calidad de datos */}
              <div 
                className="position-absolute bottom-0 end-0 m-3"
                style={{ zIndex: 3 }}
              >
                <div 
                  className={`badge ${
                    railwayData.statistics.dataQuality === 'excellent' ? 'bg-success' :
                    railwayData.statistics.dataQuality === 'good' ? 'bg-primary' :
                    railwayData.statistics.dataQuality === 'poor' ? 'bg-warning' : 'bg-secondary'
                  }`}
                  style={{ fontSize: '10px' }}
                >
                  📊 {railwayData.statistics.dataQuality}
                </div>
              </div>
            </div>
          </div>

          {/* Panel de análisis estadístico (solo visible cuando hay datos) */}
          {!isLoading && !isEmpty && !combinedError && (
            <div className="analysis-panel" style={{
              padding: '15px',
              backgroundColor: 'bg-body',
              borderTop: '1px solid #dee2e6'
            }}>
              <Row>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="mb-0 text-primary">
                      {analysisData?.total_defects || hotspots.reduce((sum, h) => sum + (h.defect_count || 0), 0)}
                    </h6>
                    <small className="text-muted">Total Defectos</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="mb-0 text-warning">
                      {analysisData?.defect_density?.toFixed(1) || '0.0'}/km
                    </h6>
                    <small className="text-muted">Densidad Defectos</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="mb-0 text-success">
                      {railwayData.data?.points?.length || 0}
                    </h6>
                    <small className="text-muted">Puntos 3D</small>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <h6 className="mb-0 text-info">
                      {analysisData?.failure_prediction?.risk_level || 
                       (hotspots.some(h => h.recommendation_priority === 'CRITICAL') ? 'HIGH' : 'MEDIUM')}
                    </h6>
                    <small className="text-muted">Nivel Riesgo</small>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>

        {/* NUEVO: Tooltip Mejorado (como en LineChart) */}
        {tooltip?.visible && tooltip.point && (
          <div
            className="position-fixed bg-dark text-white p-3 rounded shadow-lg border"
            style={{
              left: tooltip.x + 15,
              top: tooltip.y - 10,
              zIndex: 1000,
              pointerEvents: 'none',
              fontSize: '0.875rem',
              maxWidth: '350px',
              minWidth: '280px'
            }}
          >
            <div className="fw-bold mb-2 border-bottom pb-2">
              <i className="fas fa-map-marker-alt me-2"></i>
              {tooltip.point.label}
            </div>
            <div className="small">
              {tooltip.point.metadata.type === 'hotspot' ? (
                <>
                  <div className="mb-1">
                    <strong>📍 Ubicación:</strong> {tooltip.point.metadata.location}
                  </div>
                  <div className="mb-1">
                    <strong>⚠️ Prioridad:</strong>
                    <Badge 
                      bg={getPriorityColor(tooltip.point.metadata.recommendation_priority)} 
                      className="ms-2"
                    >
                      {tooltip.point.metadata.recommendation_priority}
                    </Badge>
                  </div>
                  <div className="mb-1">
                    <strong>📊 Risk Score:</strong> {(tooltip.point.metadata.risk_score || 0).toFixed(3)}
                  </div>
                  <div className="mb-1">
                    <strong>🔧 Defectos:</strong> {tooltip.point.metadata.defect_count || 0}
                  </div>
                  {tooltip.point.metadata.dominant_defects && tooltip.point.metadata.dominant_defects.length > 0 && (
                    <div className="mb-1">
                      <strong>🏷️ Tipos:</strong> {tooltip.point.metadata.dominant_defects.slice(0, 2).join(', ')}
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-top">
                    <small className="text-muted">💡 Click para ver detalles completos</small>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-1">
                    <strong>📍 KP:</strong> {tooltip.point.metadata.kp}
                  </div>
                  <div className="mb-1">
                    <strong>📊 Métrica:</strong> {tooltip.point.metadata.metric}
                  </div>
                  <div className="mb-1">
                    <strong>📈 Valor:</strong> {tooltip.point.metadata.originalValue?.toFixed(2)}
                  </div>
                  {tooltip.point.metadata.priority && (
                    <div className="mb-1">
                      <strong>⚠️ Prioridad:</strong> {tooltip.point.metadata.priority}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card.Body>

      {/* Footer with Legend and Statistics */}
      {(showLegend || showStatistics) && !isEmpty && !isLoading && (
        <Card.Footer className="bg-body">
          <Row className="g-3">
            {/* Legend */}
            {showLegend && (
              <Col md={6}>
                <h6 className="small fw-bold mb-2">
                  {chartType === 'hotspots' || chartType === 'combined' ? '🎯 Leyenda Prioridades' : '📊 Leyenda Métricas'}
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {(chartType === 'hotspots' || chartType === 'combined') && 
                    Object.entries(priorityColors).map(([priority, color]) => (
                      <div key={priority} className="d-flex align-items-center me-3">
                        <div 
                          className="rounded-circle me-1"
                          style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: color 
                          }}
                        ></div>
                        <small>{priority}</small>
                      </div>
                    ))
                  }
                  
                  {(chartType === 'technical' || chartType === 'combined') && 
                    Object.entries(metricColors)
                      .filter(([metric]) => railwayData.filters.selectedMetrics.includes(metric))
                      .map(([metric, color]) => (
                        <div key={metric} className="d-flex align-items-center me-3">
                          <div 
                            className="rounded-circle me-1"
                            style={{ 
                              width: '12px', 
                              height: '12px', 
                              backgroundColor: color 
                            }}
                          ></div>
                          <small>{metric}</small>
                        </div>
                      ))
                  }
                </div>
              </Col>
            )}

            {/* Statistics */}
            {showStatistics && (
              <Col md={6}>
                <h6 className="small fw-bold mb-2">📈 Estadísticas</h6>
                <Row className="g-2 small">
                  <Col xs={6}>
                    <strong>{railwayData.statistics.totalPoints}</strong> puntos totales
                  </Col>
                  <Col xs={6}>
                    <strong>{railwayData.statistics.averageRiskScore.toFixed(3)}</strong> risk promedio
                  </Col>
                  <Col xs={6}>
                    <strong>{railwayData.statistics.maxDefectCount}</strong> max defectos
                  </Col>
                  <Col xs={6}>
                    <strong>{Object.keys(railwayData.statistics.priorityDistribution).length}</strong> categorías
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Card.Footer>
      )}

      {/* NUEVO: Modal de Detalles del Hotspot (como en LineChart) */}
      <Modal show={showHotspotModal} onHide={handleCloseHotspotModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-map-marker-alt me-2"></i>
            Detalles del Hotspot
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedHotspot && (
            <div>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="bg-light p-3 rounded">
                    <h6 className="fw-bold mb-3">📍 Información General</h6>
                    <div className="mb-2">
                      <strong>Ubicación:</strong> {selectedHotspot.location}
                    </div>
                    <div className="mb-2">
                      <strong>ID Hotspot:</strong> {selectedHotspot.id || 'N/A'}
                    </div>
                    <div className="mb-2">
                      <strong>Prioridad:</strong>
                      <Badge 
                        bg={getPriorityColor(selectedHotspot.recommendation_priority)} 
                        className="ms-2"
                      >
                        {selectedHotspot.recommendation_priority}
                      </Badge>
                    </div>
                    {selectedHotspot.coordinates && (
                      <div className="mb-2">
                        <strong>Coordenadas:</strong><br/>
                        <small className="text-muted">
                          Lat: {selectedHotspot.coordinates.latitude.toFixed(6)}<br/>
                          Lng: {selectedHotspot.coordinates.longitude.toFixed(6)}
                        </small>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light p-3 rounded">
                    <h6 className="fw-bold mb-3">📊 Métricas de Riesgo</h6>
                    <div className="mb-2">
                      <strong>Risk Score:</strong> 
                      <span className={`ms-2 fw-bold ${
                        selectedHotspot.risk_score > 0.7 ? 'text-danger' :
                        selectedHotspot.risk_score > 0.4 ? 'text-warning' : 'text-success'
                      }`}>
                        {selectedHotspot.risk_score.toFixed(3)}
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Total Defectos:</strong> 
                      <span className="ms-2 fw-bold text-primary">
                        {selectedHotspot.defect_count}
                      </span>
                    </div>
                    <div className="progress mt-2" style={{ height: '10px' }}>
                      <div 
                        className={`progress-bar ${
                          selectedHotspot.risk_score > 0.7 ? 'bg-danger' :
                          selectedHotspot.risk_score > 0.4 ? 'bg-warning' : 'bg-success'
                        }`}
                        style={{ width: `${selectedHotspot.risk_score * 100}%` }}
                      ></div>
                    </div>
                    <small className="text-muted">Nivel de riesgo: {(selectedHotspot.risk_score * 100).toFixed(1)}%</small>
                  </div>
                </Col>
              </Row>

              {/* Defectos Dominantes */}
              {selectedHotspot.dominant_defects && selectedHotspot.dominant_defects.length > 0 && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <div className="bg-light p-3 rounded">
                      <h6 className="fw-bold mb-3">🏷️ Tipos de Defectos Dominantes</h6>
                      <div className="d-flex flex-wrap gap-2">
                        {selectedHotspot.dominant_defects.map((defect, index) => (
                          <Badge key={index} bg="info" className="me-1 mb-1">
                            {defect}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Desglose de Severidad */}
              {selectedHotspot.severity_breakdown && Object.keys(selectedHotspot.severity_breakdown).length > 0 && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <div className="bg-light p-3 rounded">
                      <h6 className="fw-bold mb-3">📈 Desglose de Severidad</h6>
                      <Row>
                        {Object.entries(selectedHotspot.severity_breakdown).map(([severity, count]) => (
                          <Col key={severity} xs={6} md={3} className="mb-2">
                            <div className="text-center">
                              <div className="fw-bold text-primary">{count}</div>
                              <small className="text-muted">{severity}</small>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Historial de Mantenimiento */}
              {selectedHotspot.maintenance_history && selectedHotspot.maintenance_history.length > 0 && (
                <Row>
                  <Col xs={12}>
                    <div className="bg-light p-3 rounded">
                      <h6 className="fw-bold mb-3">🔧 Historial de Mantenimiento</h6>
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {selectedHotspot.maintenance_history.map((record, index) => (
                          <div key={index} className="mb-2 p-2 bg-white rounded border-start border-3 border-info">
                            <small>{record}</small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseHotspotModal}>
            <i className="fas fa-times me-1"></i>
            Cerrar
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              if (selectedHotspot && onHotspotSelect) {
                onHotspotSelect(selectedHotspot);
              }
              handleCloseHotspotModal();
            }}
          >
            <i className="fas fa-map-marker-alt me-1"></i>
            Ver en Mapa
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Filters Modal */}
      <Modal show={showFiltersModal} onHide={() => setShowFiltersModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-filter me-2"></i>
            Filtros del Gráfico 3D
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-4">
            {/* Hotspots Limit */}
            <Col xs={12}>
              <h6>🎯 Límite de Hotspots</h6>
              <Form.Select
                value={railwayData.filters.hotspotsLimit}
                onChange={(e) => railwayData.actions.updateHotspotsLimit(e.target.value)}
              >
                {Object.entries(hotspotsLimits).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Form.Select>
            </Col>

            {/* Priority Filters */}
            <Col md={6}>
              <h6>⚠️ Filtros de Prioridad</h6>
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(priority => (
                <Form.Check
                  key={priority}
                  type="checkbox"
                  label={priority}
                  checked={railwayData.filters.selectedPriorities.includes(priority)}
                  onChange={(e) => {
                    const newPriorities = e.target.checked
                      ? [...railwayData.filters.selectedPriorities, priority]
                      : railwayData.filters.selectedPriorities.filter(p => p !== priority);
                    railwayData.actions.updatePriorityFilter(newPriorities);
                  }}
                />
              ))}
            </Col>

            {/* Metric Filters */}
            {(chartType === 'technical' || chartType === 'combined') && (
              <Col md={6}>
                <h6>📊 Métricas Técnicas</h6>
                {railwayData.metadata.availableMetrics.map(metric => (
                  <Form.Check
                    key={metric}
                    type="checkbox"
                    label={metric}
                    checked={railwayData.filters.selectedMetrics.includes(metric)}
                    onChange={(e) => {
                      const newMetrics = e.target.checked
                        ? [...railwayData.filters.selectedMetrics, metric]
                        : railwayData.filters.selectedMetrics.filter(m => m !== metric);
                      railwayData.actions.updateSelectedMetrics(newMetrics);
                    }}
                  />
                ))}
              </Col>
            )}

            {/* Range Filters */}
            <Col xs={12}>
              <h6>📏 Filtros de Rango</h6>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Risk Score: {railwayData.filters.riskRange[0]} - {railwayData.filters.riskRange[1]}</Form.Label>
                  <Form.Range
                    min={0}
                    max={1}
                    step={0.1}
                    value={railwayData.filters.riskRange[1]}
                    onChange={(e) => railwayData.actions.updateRiskRange([railwayData.filters.riskRange[0], parseFloat(e.target.value)])}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Defect Count: {railwayData.filters.defectCountRange[0]} - {railwayData.filters.defectCountRange[1]}</Form.Label>
                  <Form.Range
                    min={0}
                    max={100}
                    value={railwayData.filters.defectCountRange[1]}
                    onChange={(e) => railwayData.actions.updateDefectCountRange([railwayData.filters.defectCountRange[0], parseInt(e.target.value)])}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>KP Range: {railwayData.filters.kpRange[0]} - {railwayData.filters.kpRange[1]}</Form.Label>
                  <Form.Range
                    min={0}
                    max={100}
                    value={railwayData.filters.kpRange[1]}
                    onChange={(e) => railwayData.actions.updateKpRange([railwayData.filters.kpRange[0], parseInt(e.target.value)])}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={railwayData.actions.resetFilters}>
            <i className="fas fa-undo me-1"></i>
            Reset Filtros
          </Button>
          <Button variant="primary" onClick={() => setShowFiltersModal(false)}>
            <i className="fas fa-check me-1"></i>
            Aplicar Filtros
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};